---
id: apps-app-react-src-rezi-app-dashboard-doc
title: Rezi App Dashboard
owner: Luc Lemerez
status: Active
lastReviewed: 2026-01-27
visibility: public
tags:
  - app-react
  - dashboard
  - rezi-app
---

## Product Overview

Dashboard experience for logged-in users to manage resumes and related
documents while surfacing entry points into core resume and job search
workflows.

### Primary Workflow
Users land on the dashboard, switch between resume-related lists, and use the
hero navigation to jump into resume tools, job search, and review experiences.

### Business Logic
Navigation and call-to-action items are conditionally shown based on RMS
configuration, feature availability, and whether job recommendations are
available for the user. Document tabs for cover and resignation letters are
hidden when the RMS configuration disables them, and promotion banners are
shown or suppressed depending on billing plan and RMS promotion settings.

### Outcomes
The dashboard acts as the primary hub for document management and discovery of
adjacent workflows, keeping users oriented and providing fast access to the
most relevant resume and job search actions.

### Out of Scope
Authentication flows and account settings live outside this dashboard module,
as do non-dashboard product areas such as the full job search pages.

## Technical Overview

The dashboard entry point in `apps/app-react/src/rezi-app/dashboard/dashboard.tsx`
provides a React context for view and sorting preferences, backed by
localStorage and sessionStorage, and renders the lazy-loaded layout shell from
`apps/app-react/src/rezi-app/dashboard/layout.tsx`.

### Entry Points
Route-level rendering and the list views are handled in
`apps/app-react/src/rezi-app/dashboard/layout.tsx`, which configures the
dashboard routes for resumes, cover letters, and resignation letters. The hero
navigation in `apps/app-react/src/rezi-app/dashboard/hero-nav.tsx` supplies
dashboard call-to-action cards and routes into adjacent flows.

### Key Modules
`apps/app-react/src/rezi-app/dashboard/components/list-items` renders the
document lists, while shared layout and navigation come from
`@rezi/features/hoc/layout` and `@rezi/features/nav-bar`. The dashboard uses
MobX stores from `@rezi/stores/providers/root-store-provider` for billing, RMS
configuration, and resume state.

### Data and Services
The hero navigation uses job preference and recommendation hooks in
`src/rezi-app/jobs/hooks` and tracks user interaction via
`@rezi/utils/send-event-tracking`. View preferences and sorting state are
persisted with localStorage and sessionStorage.

### Integrations and Side Effects
The layout triggers resume creation when the `imp-linkedin-rezi` localStorage
flag is present, and it conditionally displays banners based on RMS promotion
settings and billing plan information. Translation strings are resolved via
`react-i18next` in the layout and hero navigation modules.

### Constraints and Risks
Dashboard visibility and behavior depend on RMS configuration flags and user
billing state, so regressions in those stores can hide navigation or
misconfigure banners.

## Changelog

- 2026-01-27: Expanded product and technical overviews to cover business logic and key code paths.
- 2026-01-26: Initial documentation.
